import Phaser from 'phaser';
import { EV } from '../game/events';
import { NET } from '../data/balance';
import { parseNetMsg, type AuthCreds, type FriendView, type NetMsg, type RemotePose } from './protocol';

export class NetClient {
  socket: WebSocket | null = null;
  id = '';
  name = '';
  account = '';
  online = false;
  others: RemotePose[] = [];
  party: { leader: string; members: { id: string; name: string }[] } | null = null;
  friends: FriendView[] = [];
  incoming: string[] = [];
  correction: { x: number; y: number } | null = null;
  welcomeSave: unknown | null = null;
  readonly claims = new Map<string, { ownerId: string; ownerName: string; until: number }>();
  readonly events = new Phaser.Events.EventEmitter();

  private poseAcc = 0;
  private posed = false;
  private manualUrl = '';

  get connected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  /** https 터널 주소를 wss 로 바꾼다. 비우면 자동(로컬 또는 같은 호스트). */
  static normalizeUrl(raw: string): string {
    return NetClient.parseServerUrl(raw).url;
  }

  /** 붙여 넣은 로그에서 URL 만 고르고, http(s) 는 ws(s) 로 바꾼다. */
  static parseServerUrl(raw: string): { url: string; error?: string } {
    let s = raw.trim();
    if (!s) return { url: '' };
    const found = s.match(/https?:\/\/[^\s"'<>]+/i) ?? s.match(/wss?:\/\/[^\s"'<>]+/i);
    if (found) s = found[0];
    s = s.replace(/[),.;]+$/g, '');
    if (!/^(wss?|https?):\/\//i.test(s)) {
      if (/^[a-z0-9.-]+(?::\d+)?(?:\/\S*)?$/i.test(s)) s = `https://${s}`;
      else {
        return {
          url: '',
          error: `서버 주소 형식이 아닙니다. https://….trycloudflare.com 전체를 넣으세요 (받은 값: ${s.slice(0, 80)})`,
        };
      }
    }
    if (s.startsWith('https://')) s = `wss://${s.slice('https://'.length)}`;
    else if (s.startsWith('http://')) s = `ws://${s.slice('http://'.length)}`;
    return { url: s.replace(/\/+$/, '') };
  }

  url(): string {
    if (this.manualUrl) return this.manualUrl;
    const env = import.meta.env.VITE_NET_URL as string | undefined;
    if (env) return env;
    if (typeof location !== 'undefined') {
      const host = location.hostname;
      const loopback = host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost');
      if (!loopback) {
        const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
        const path = (import.meta.env.VITE_NET_PATH as string | undefined) || '';
        if (location.protocol === 'https:') return `${proto}//${location.host}${path}`;
        if (location.port === String(NET.port) || location.port === '') {
          return `${proto}//${location.host}${path}`;
        }
        return `${proto}//${host}:${NET.port}${path}`;
      }
    }
    return `ws://127.0.0.1:${NET.port}`;
  }

  /**
   * 성공이면 null, 실패면 화면에 그대로 보여줄 이유.
   */
  connect(auth: AuthCreds, jobId: string, save: unknown): Promise<string | null> {
    this.disconnect();
    this.online = true;
    const parsed = NetClient.parseServerUrl(auth.serverUrl ?? '');
    if (parsed.error) {
      this.online = false;
      return Promise.resolve(parsed.error);
    }
    this.manualUrl = parsed.url;
    this.name = auth.nick || auth.account;
    this.account = auth.account;
    const target = this.url();
    return new Promise((resolve) => {
      let settled = false;
      const done = (reason: string | null): void => {
        if (settled) return;
        settled = true;
        resolve(reason);
      };
      try {
        this.socket = new WebSocket(target);
      } catch (err) {
        this.online = false;
        done(
          `웹소켓 주소를 열 수 없습니다 (${target}). ${err instanceof Error ? err.message : String(err)}`,
        );
        return;
      }
      const timer = window.setTimeout(() => {
        this.disconnect();
        done(
          `서버에 10초 동안 연결되지 않았습니다. 주소=${target} · 호스트에서 서버·터널이 켜져 있는지, URL 이 바뀌지 않았는지 확인하세요`,
        );
      }, 10000);
      this.socket.addEventListener('open', () => {
        this.send({
          t: 'hello',
          mode: auth.mode,
          account: auth.account,
          password: auth.password,
          nick: auth.nick,
          jobId,
          save,
        });
      });
      this.socket.addEventListener('message', (ev) => {
        const msg = parseNetMsg(String(ev.data));
        if (!msg) return;
        if (msg.t === 'welcome') {
          window.clearTimeout(timer);
          this.id = msg.id;
          this.name = msg.name;
          this.account = msg.account;
          this.welcomeSave = msg.save;
          this.events.emit(EV.netStatus, { connected: true, name: msg.name });
          done(null);
          return;
        }
        if (msg.t === 'reject') {
          window.clearTimeout(timer);
          this.disconnect();
          const how = auth.mode === 'register' ? '가입' : '로그인';
          done(`${how} 거부: ${msg.reason} (주소 ${target})`);
          return;
        }
        this.handle(msg);
      });
      this.socket.addEventListener('close', (ev) => {
        window.clearTimeout(timer);
        if (this.online) this.events.emit(EV.netStatus, { connected: false });
        this.socket = null;
        if (!settled) {
          done(
            `서버와 연결이 끊겼습니다 (코드 ${ev.code}${ev.reason ? `: ${ev.reason}` : ''}). 주소=${target} · 터널/서버가 꺼졌거나 주소가 만료됐을 수 있습니다`,
          );
        }
      });
      this.socket.addEventListener('error', () => {
        window.clearTimeout(timer);
        if (!settled) {
          this.disconnect();
          done(
            `웹소켓 연결 실패. 주소=${target} · https 터널이면 wss 로 붙습니다. cloudflared 와 서버 스크립트가 둘 다 켜져 있는지 확인하세요`,
          );
        }
      });
    });
  }

  disconnect(): void {
    this.online = false;
    this.id = '';
    this.posed = false;
    this.others = [];
    this.party = null;
    this.friends = [];
    this.incoming = [];
    this.manualUrl = '';
    this.claims.clear();
    this.socket?.close();
    this.socket = null;
  }

  send(msg: NetMsg): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(JSON.stringify(msg));
  }

  tickPose(
    dt: number,
    pose: { mapId: string; x: number; y: number; vx: number; vy: number; facing: 1 | -1; mode: string },
  ): void {
    if (!this.connected) return;
    this.poseAcc += dt;
    const interval = 1000 / NET.poseHz;
    if (this.poseAcc < interval) return;
    this.poseAcc = 0;
    this.posed = true;
    this.send({ t: 'pose', ...pose });
  }

  consumeCorrection(): { x: number; y: number } | null {
    if (!this.posed) return null;
    const c = this.correction;
    this.correction = null;
    return c;
  }

  canLoot(key: string, now: number): boolean {
    if (!this.connected) return true;
    const c = this.claims.get(key);
    if (!c || c.until < now) return true;
    if (c.ownerId === this.id) return true;
    return this.isPartyMember(c.ownerId);
  }

  isPartyMember(id: string): boolean {
    return this.party?.members.some((m) => m.id === id) ?? false;
  }

  claim(key: string): void {
    if (!this.connected || !this.id) return;
    this.claims.set(key, {
      ownerId: this.id,
      ownerName: this.name,
      until: performance.now() + NET.claimMs,
    });
    this.send({ t: 'claim', key });
  }

  notifyKill(key: string, exp: number, mapId: string, x: number, y: number): void {
    this.send({ t: 'kill', key, exp, mapId, x, y });
  }

  pushSave(save: unknown): void {
    this.send({ t: 'save', save });
  }

  chat(text: string): void {
    const trimmed = text.trim().slice(0, NET.chatMax);
    if (!trimmed) return;
    this.send({ t: 'chat', text: trimmed });
  }

  invite(name: string): void {
    this.send({ t: 'partyInvite', name });
  }

  setNick(nick: string): void {
    this.send({ t: 'setNick', nick });
  }

  friendAdd(nick: string): void {
    this.send({ t: 'friendAdd', nick });
  }

  friendAccept(nick: string): void {
    this.send({ t: 'friendAccept', nick });
  }

  friendRemove(nick: string): void {
    this.send({ t: 'friendRemove', nick });
  }

  whisper(to: string, text: string): void {
    this.send({ t: 'whisper', to, text });
  }

  private handle(msg: NetMsg): void {
    switch (msg.t) {
      case 'snapshot':
        this.others = msg.others;
        this.correction = msg.you;
        break;
      case 'chatLine':
        this.events.emit(EV.netChat, { from: msg.from, text: msg.text, whisper: Boolean(msg.whisper) });
        break;
      case 'party':
        this.party = { leader: msg.leader, members: msg.members };
        this.events.emit(EV.netParty, this.party);
        break;
      case 'claimOk':
        this.claims.set(msg.key, {
          ownerId: msg.ownerId,
          ownerName: msg.ownerName,
          until: performance.now() + NET.claimMs,
        });
        break;
      case 'claimDeny':
        this.claims.delete(msg.key);
        this.events.emit(EV.toast, { message: `${msg.ownerName} 이(가) 선점 중`, tone: 'warn' });
        break;
      case 'shareExp':
        this.events.emit(EV.netShareExp, { amount: msg.amount, from: msg.from });
        break;
      case 'nickOk':
        this.name = msg.name;
        this.events.emit(EV.netStatus, { connected: true, name: msg.name });
        break;
      case 'friendList':
        this.friends = msg.friends;
        this.incoming = msg.incoming;
        this.events.emit(EV.netFriends, { friends: msg.friends, incoming: msg.incoming });
        break;
      default:
        break;
    }
  }
}

export const net = new NetClient();
